package pl.zar.tikthrow.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import io.ktor.client.HttpClient
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import pl.zar.tikthrow.shared.dto.ApiError
import pl.zar.tikthrow.shared.dto.ApiKeyInfo
import pl.zar.tikthrow.shared.dto.CreateApiKeyRequest
import pl.zar.tikthrow.shared.dto.CreateApiKeyResponse
import pl.zar.tikthrow.shared.paging.PagedResult
import pl.zar.tikthrow.util.apiBaseUrl
import pl.zar.tikthrow.util.currentAuthToken

private val keysJson = Json { ignoreUnknownKeys = true }

/**
 * T201 (spec 002-api-mcp, US1): mint / list / revoke API keys from the app.
 *
 * Key management is **session-only** on the server (`POST|GET|DELETE
 * /api/v1/keys` answer 403 `session_required` for an agent token — an agent must
 * not be able to mint tokens for itself), so this view model always
 * authenticates with the logged-in session (`Bearer <session>`), exactly like
 * the rest of the app's calls.
 *
 * The raw token is returned by the server exactly once: it is held in
 * [ApiKeysUiState.freshKey] and dropped as soon as the dialog is dismissed —
 * never persisted, never logged.
 */
data class ApiKeysUiState(
    val loading: Boolean = true,
    val keys: List<ApiKeyInfo> = emptyList(),
    val error: String? = null,
    val creating: Boolean = false,
    val freshKey: String? = null,
)

class ApiKeysViewModel : ViewModel() {

    private val _state = MutableStateFlow(ApiKeysUiState())
    val state: StateFlow<ApiKeysUiState> = _state.asStateFlow()

    init {
        refresh()
    }

    /** `GET /api/v1/keys` — masked metadata only (no raw token, no hash). */
    fun refresh() {
        val token = currentAuthToken()
        if (token == null) {
            _state.value = _state.value.copy(loading = false, error = "Brak sesji — zaloguj się ponownie")
            return
        }
        viewModelScope.launch {
            _state.value = _state.value.copy(loading = true, error = null)
            runCatching {
                val client = HttpClient { expectSuccess = false }
                val response = client.get("$apiBaseUrl/api/v1/keys") {
                    header(HttpHeaders.Authorization, "Bearer $token")
                }
                val body = response.bodyAsText()
                client.close()
                if (response.status.isSuccess()) {
                    val page = keysJson.decodeFromString<PagedResult<ApiKeyInfo>>(body)
                    _state.value = _state.value.copy(loading = false, keys = page.items)
                } else {
                    _state.value = _state.value.copy(loading = false, error = apiErrorMessage(body))
                }
            }.onFailure { failure ->
                _state.value = _state.value.copy(
                    loading = false,
                    error = failure.message ?: "Nie udało się pobrać kluczy",
                )
            }
        }
    }

    /** `POST /api/v1/keys` — the answer carries the raw key, shown exactly once. */
    fun create(name: String) {
        val trimmed = name.trim()
        if (trimmed.isEmpty()) {
            _state.value = _state.value.copy(error = "Nazwa klucza nie może być pusta")
            return
        }
        val token = currentAuthToken()
        if (token == null) {
            _state.value = _state.value.copy(error = "Brak sesji — zaloguj się ponownie")
            return
        }
        viewModelScope.launch {
            _state.value = _state.value.copy(creating = true, error = null)
            runCatching {
                val client = HttpClient { expectSuccess = false }
                val response = client.post("$apiBaseUrl/api/v1/keys") {
                    header(HttpHeaders.Authorization, "Bearer $token")
                    contentType(ContentType.Application.Json)
                    setBody(keysJson.encodeToString(CreateApiKeyRequest(name = trimmed)))
                }
                val body = response.bodyAsText()
                client.close()
                if (response.status.isSuccess()) {
                    val created = keysJson.decodeFromString<CreateApiKeyResponse>(body)
                    _state.value = _state.value.copy(
                        creating = false,
                        freshKey = created.key,
                        keys = listOf(created.info) + _state.value.keys,
                    )
                } else {
                    _state.value = _state.value.copy(creating = false, error = apiErrorMessage(body))
                }
            }.onFailure { failure ->
                _state.value = _state.value.copy(
                    creating = false,
                    error = failure.message ?: "Nie udało się wygenerować klucza",
                )
            }
        }
    }

    /** `DELETE /api/v1/keys/{id}` — revoke (the listing keeps the row, marked revoked). */
    fun revoke(id: String) {
        val token = currentAuthToken()
        if (token == null) {
            _state.value = _state.value.copy(error = "Brak sesji — zaloguj się ponownie")
            return
        }
        viewModelScope.launch {
            _state.value = _state.value.copy(error = null)
            runCatching {
                val client = HttpClient { expectSuccess = false }
                val response = client.delete("$apiBaseUrl/api/v1/keys/$id") {
                    header(HttpHeaders.Authorization, "Bearer $token")
                }
                val body = response.bodyAsText()
                client.close()
                if (!response.status.isSuccess()) {
                    _state.value = _state.value.copy(error = apiErrorMessage(body))
                }
            }.onFailure { failure ->
                _state.value = _state.value.copy(error = failure.message ?: "Nie udało się odwołać klucza")
            }
            refresh()
        }
    }

    /** Drops the raw key from memory once the owner has copied it. */
    fun dismissFreshKey() {
        _state.value = _state.value.copy(freshKey = null)
    }

    fun dismissError() {
        _state.value = _state.value.copy(error = null)
    }
}

/** `/api/v1` answers every 4xx/5xx with the structural [ApiError]. */
private fun apiErrorMessage(body: String): String =
    runCatching { keysJson.decodeFromString<ApiError>(body).message }
        .getOrElse { "Błąd serwera (${body.take(120)})" }
